import React, { useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useCan } from '../hooks/useCan';
import useUsagePages from '../hooks/useUsagePages';
import useQueryParamState from '../hooks/useQueryParamState';
import UsagePeriodSelector from '../components/usage/UsagePeriodSelector';
import UsageKpiCard from '../components/usage/UsageKpiCard';
import UsagePagesChart from '../components/usage/UsagePagesChart';
import UsageTypeBars from '../components/usage/UsageTypeBars';
import UsageTypeTable from '../components/usage/UsageTypeTable';
import UsageDocumentsTable from '../components/usage/UsageDocumentsTable';
import { typeLabel } from '../utils/categoricalColors';
import { formatDecimal1, formatInt, formatRange } from '../utils/numberFormat';
import { DEFAULT_PRESET, granularityLabel } from '../utils/usagePeriod';
import { downloadCsv, toCsvString } from '../utils/csvExport';
import dayjs from 'dayjs';

const Sezione = ({ titolo, hint, azione, children }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
      <Typography variant="h6">{titolo}</Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      )}
      {azione && <Box sx={{ ml: 'auto' }}>{azione}</Box>}
    </Stack>
    {children}
  </Paper>
);

/** Errore circoscritto alla sezione: un guasto sull'elenco non deve portarsi via KPI e grafico. */
const ErroreSezione = ({ onRetry }) => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={onRetry}>
        Riprova
      </Button>
    }
  >
    Non è stato possibile caricare questa sezione.
  </Alert>
);

/**
 * Pagine elaborate (AUTO-213): quante pagine l'installazione ha elaborato e di che tipo di documento.
 *
 * Solo pagine: niente token, modelli, provider o costi, e nessuna soglia o residuo — l'installazione
 * non conosce i limiti contrattuali, che vivono su Automate Dashboard. L'unico confronto è con il
 * periodo precedente.
 */
const UsagePages = () => {
  const [preset, setPreset] = useQueryParamState('period', DEFAULT_PRESET);
  // Il permesso si legge prima dell'hook e lo governa: i React hook non possono stare dentro un
  // `if`, ma le chiamate al backend sì, e per chi non ha il permesso sarebbero quattro 403.
  const can = useCan();
  const canRead = can('llm_usage.read');
  const {
    periodo,
    periodoPrecedente,
    granularita,
    summary,
    timeseries,
    byCodexType,
    documents,
    setPage,
    setSize,
    cambiaPreset,
    ricarica,
  } = useUsagePages(preset, { enabled: canRead });

  const cambiaPeriodo = useCallback(
    (nuovoPreset) => {
      setPreset(nuovoPreset);
      cambiaPreset(nuovoPreset);
    },
    [cambiaPreset, setPreset]
  );

  // useMemo: senza, il fallback [] sarebbe un array nuovo a ogni render e l'export si
  // ricreerebbe di continuo.
  const righeTipo = useMemo(() => byCodexType.data ?? [], [byCodexType.data]);

  const esportaCsv = useCallback(() => {
    const colonne = [
      { header: 'Tipo di documento', value: (row) => typeLabel(row) },
      { header: 'Documenti', value: (row) => row.documents },
      { header: 'Pagine', value: (row) => row.pages },
      { header: 'Quota %', value: (row) => formatDecimal1(row.sharePercent) },
      { header: 'Media pagine per documento', value: (row) => formatDecimal1(row.avgPagesPerDocument) },
    ];

    const totalePagine = righeTipo.reduce((acc, row) => acc + row.pages, 0);
    const totaleDocumenti = righeTipo.reduce((acc, row) => acc + row.documents, 0);

    // La riga di totale passa dalle stesse colonne delle altre: se un giorno cambia l'escaping,
    // cambia anche per lei.
    const rigaTotale = {
      codexTypeCode: 'Totale',
      documents: totaleDocumenti,
      pages: totalePagine,
      sharePercent: righeTipo.length ? 100 : 0,
      avgPagesPerDocument: totaleDocumenti > 0 ? totalePagine / totaleDocumenti : 0,
    };

    // Il periodo di confronto lo dichiara il backend nella risposta: è quello su cui ha davvero
    // calcolato le variazioni. Ricalcolarlo qui darebbe due sorgenti per lo stesso numero, ed è
    // così che un giorno il CSV finisce per raccontare una finestra diversa da quella usata.
    // Il calcolo locale resta solo come ripiego se il riepilogo non è ancora arrivato.
    const confronto = summary.data?.previous;
    const confrontoDa = confronto?.dateFrom ?? periodoPrecedente.from;
    const confrontoA = confronto?.dateTo ?? periodoPrecedente.to;

    const csv = toCsvString([...righeTipo, rigaTotale], colonne, {
      preamble: [
        ['Pagine elaborate'],
        ['Periodo', formatRange(periodo.from, periodo.to)],
        // «di pari lunghezza» perché 18 giorni di settembre si confrontano con i 18 giorni
        // precedenti (14-31 agosto), non con il mese di agosto intero.
        ['Periodo di confronto (di pari lunghezza)', formatRange(confrontoDa, confrontoA)],
        [],
      ],
    });

    const nome = `pagine-elaborate_${dayjs(periodo.from).format('YYYY-MM-DD')}_${dayjs(periodo.to).format('YYYY-MM-DD')}.csv`;

    downloadCsv(nome, csv);
  }, [periodo.from, periodo.to, periodoPrecedente.from, periodoPrecedente.to, righeTipo, summary.data]);

  // Difesa in profondità: la rotta è già guardata in routeGuards.js e il backend risponde 403,
  // ma la pagina non deve poter comparire per chi non la può vedere.
  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Non hai i permessi per questa sezione
          </Typography>
          <Typography color="text.secondary">
            Le pagine elaborate sono visibili solo agli amministratori dell&apos;installazione.
            Se ti serve accedere, chiedi a un amministratore.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const riepilogo = summary.data;

  return (
    <Box sx={{ p: 3 }}>
      {/*
        Stessa impaginazione del mockup: titolo a sinistra, comandi allineati a destra sulla stessa
        riga (`.page-head` è flex, `.head-actions` ha margin-left:auto). Sotto il breakpoint md i
        comandi vanno a capo a tutta larghezza, come fa la media query del mockup.
      */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4">Pagine elaborate</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: '62ch' }}>
            Quante pagine Automate ha elaborato in questa installazione, suddivise per tipo di documento.
          </Typography>
        </Box>

        <Box sx={{ ml: { md: 'auto' }, width: { xs: '100%', md: 'auto' } }}>
          <UsagePeriodSelector
            preset={preset}
            onPresetChange={cambiaPeriodo}
            from={periodo.from}
            to={periodo.to}
            onExport={esportaCsv}
            exportDisabled={byCodexType.loading || !righeTipo.length}
          />
        </Box>
      </Box>

      {summary.error ? (
        <Box sx={{ mb: 3 }}>
          <ErroreSezione onRetry={() => ricarica(['summary'])} />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <UsageKpiCard
              lead
              label="Pagine elaborate"
              value={formatInt(riepilogo?.pages)}
              delta={riepilogo?.pagesChangePercent}
              footer="rispetto al periodo precedente"
              loading={summary.loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <UsageKpiCard
              label="Documenti elaborati"
              value={formatInt(riepilogo?.documents)}
              delta={riepilogo?.documentsChangePercent}
              footer="rispetto al periodo precedente"
              loading={summary.loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <UsageKpiCard
              label="Media pagine per documento"
              value={formatDecimal1(riepilogo?.avgPagesPerDocument)}
              footer={
                riepilogo?.documentsWithoutPageCount
                  ? `${formatInt(riepilogo.documentsWithoutPageCount)} documenti senza pagine conteggiate`
                  : 'su tutti i documenti del periodo'
              }
              loading={summary.loading}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 3 }}>
        <Sezione
          titolo={`Pagine elaborate ${granularityLabel(granularita)}`}
          hint="per tipo di documento"
        >
          {timeseries.error ? (
            <ErroreSezione onRetry={() => ricarica(['timeseries'])} />
          ) : timeseries.loading ? (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <UsagePagesChart timeseries={timeseries.data} granularity={granularita} />
          )}
        </Sezione>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Sezione titolo="Per tipo di documento" hint="pagine nel periodo">
            {byCodexType.error ? (
              <ErroreSezione onRetry={() => ricarica(['byCodexType'])} />
            ) : byCodexType.loading ? (
              <Stack spacing={2}>
                <Skeleton variant="rectangular" height={48} />
                <Skeleton variant="rectangular" height={48} />
                <Skeleton variant="rectangular" height={48} />
              </Stack>
            ) : (
              <UsageTypeBars rows={righeTipo} />
            )}
          </Sezione>
        </Grid>

        <Grid item xs={12} md={6}>
          <Sezione titolo="Riepilogo" hint="stessi dati in tabella">
            {byCodexType.error ? (
              <ErroreSezione onRetry={() => ricarica(['byCodexType'])} />
            ) : byCodexType.loading ? (
              <Skeleton variant="rectangular" height={180} />
            ) : (
              <UsageTypeTable rows={righeTipo} />
            )}
          </Sezione>
        </Grid>
      </Grid>

      <Sezione titolo="Documenti elaborati" hint="i più recenti per primi">
        {documents.error ? (
          <ErroreSezione onRetry={() => ricarica(['documents'])} />
        ) : documents.loading ? (
          <Skeleton variant="rectangular" height={220} />
        ) : (
          <UsageDocumentsTable
            page={documents.data}
            onPageChange={setPage}
            onSizeChange={(nuovaDimensione) => {
              setSize(nuovaDimensione);
              setPage(0);
            }}
          />
        )}
      </Sezione>
    </Box>
  );
};

export default UsagePages;
